import { useEffect, useRef, useState } from "react";
import { copy } from "../copy";
import {
  shortDate,
  type AdminContest,
  type AdminUser,
  type AdminWithdrawal,
  type GoalCount,
  type Goals,
  type Platform,
  type PlatformPeriod,
  type Usage,
  type WithdrawalState,
} from "./api";
import { hintClass, labelClass } from "./ContestForm";
import { hundredths } from "./MarketPanel";
import { Chip, Empty, ghostButton, inputClass, primaryButton, smallButton, tdClass, thClass } from "./ui";

const tableWrap = "bf-no-scrollbar overflow-x-auto";
const rowClass = "border-b border-ground-edge last:border-b-0";

export function WonTable({ rows }: { rows: AdminContest[] }) {
  const t = copy.admin.won;
  if (rows.length === 0) return <Empty icon="trophy" title={t.empty} />;
  return (
    <div className={tableWrap}>
      <table className="w-full min-w-[720px] border-collapse">
        <thead className="border-b border-ground-edge">
          <tr>
            {Object.values(t.columns).map((c) => (
              <th key={c} className={thClass}>
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.id} data-testid="won-row" className={rowClass}>
              <td className={tdClass}>
                <a href={`/contest/${c.slug || c.id}`} className="bf-focusable font-bold text-gold hover:text-gold-light">
                  {c.title}
                </a>
                <span className="block break-all text-[11px] text-chrome-dim">{c.id}</span>
              </td>
              <td className={tdClass}>{c.winner?.username}</td>
              <td className={`${tdClass} bf-tabular text-money`}>{c.cash_prize.display}</td>
              <td className={`${tdClass} bf-tabular`}>{c.pot.display}</td>
              <td className={`${tdClass} bf-tabular`}>{c.contestants}</td>
              <td className={tdClass}>{shortDate(c.won_at ?? c.end_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function UsersTable({
  users,
  busy,
  onLogin,
  onDeactivate,
  onReactivate,
}: {
  users: AdminUser[];
  busy: boolean;
  onLogin: (u: AdminUser) => void;
  onDeactivate: (u: AdminUser) => void;
  onReactivate: (u: AdminUser) => void;
}) {
  const t = copy.admin.users;
  if (users.length === 0) return <Empty icon="team" title={t.empty} />;
  return (
    <div className={tableWrap}>
      <table className="w-full min-w-[760px] border-collapse">
        <thead className="border-b border-ground-edge">
          <tr>
            <th className={thClass}>{t.columns.user}</th>
            <th className={thClass}>{t.columns.contact}</th>
            <th className={thClass}>{t.columns.balance}</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id} data-testid="user-row" className={rowClass}>
              <td className={tdClass}>
                <p className="font-bold">{u.username}</p>
                <p className="text-[12px] text-chrome-dim">{t.joined(shortDate(u.joined_at))}</p>
                <p className="bf-tabular text-[12px] text-chrome-dim">{t.counts(u.submissions, u.reservations)}</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {u.is_admin && <Chip tone="gold">{t.adminBadge}</Chip>}
                  {!u.is_active && <Chip tone="alert">{t.deactivated}</Chip>}
                  {!u.email_verified && <Chip tone="dim">{t.unverified}</Chip>}
                </div>
                {!u.is_admin && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {u.is_active && (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => onLogin(u)}
                        className={`${smallButton} border-money/50 text-money hover:bg-money hover:text-ground`}
                      >
                        {t.login}
                      </button>
                    )}
                    {u.is_active ? (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => onDeactivate(u)}
                        className={`${smallButton} border-alert/50 text-alert hover:bg-alert hover:text-ground`}
                      >
                        {t.deactivate}
                      </button>
                    ) : (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => onReactivate(u)}
                        className={`${smallButton} border-ground-edge text-chrome-dim hover:border-gold/50 hover:text-gold`}
                      >
                        {t.reactivate}
                      </button>
                    )}
                  </div>
                )}
              </td>
              <td className={tdClass}>
                <p>{u.email}</p>
                {u.paypal_email && <p className="mt-1 text-[12px] text-chrome-dim">{t.paypal(u.paypal_email)}</p>}
                {u.instagram && <p className="mt-1 text-[12px] text-chrome-dim">{t.instagram(u.instagram)}</p>}
              </td>
              <td className={`${tdClass} bf-tabular`}>{u.balance_display}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function WithdrawalsTable({
  rows,
  state,
  busy,
  onPaid,
  onReject,
}: {
  rows: AdminWithdrawal[];
  state: WithdrawalState;
  busy: boolean;
  onPaid: (w: AdminWithdrawal) => void;
  onReject: (w: AdminWithdrawal) => void;
}) {
  const t = copy.admin.withdrawals;
  if (rows.length === 0) return <Empty icon="wallet" title={t.empty} />;
  return (
    <div className={tableWrap}>
      <table className="w-full min-w-[760px] border-collapse">
        <thead className="border-b border-ground-edge">
          <tr>
            {Object.values(t.columns).map((c) => (
              <th key={c} className={thClass}>
                {c}
              </th>
            ))}
            {state === "pending" && <th className={thClass} aria-hidden="true" />}
          </tr>
        </thead>
        <tbody>
          {rows.map((w) => (
            <tr key={w.id} data-testid="withdrawal-row" className={rowClass}>
              <td className={tdClass}>
                <p className="font-bold">{w.username}</p>
                <p className="text-[12px] text-chrome-dim">{w.email}</p>
              </td>
              <td className={tdClass}>{w.paypal_email}</td>
              <td className={`${tdClass} bf-tabular text-money`}>{w.amount_display}</td>
              <td className={tdClass}>{shortDate(w.requested_at)}</td>
              <td className={tdClass}>
                <Chip tone={w.state === "completed" ? "money" : w.state === "rejected" ? "alert" : "gold"}>
                  {t.states[w.state] ?? w.state}
                </Chip>
                {w.paid_at && <p className="mt-1 text-[11px] text-chrome-dim">{shortDate(w.paid_at)}</p>}
                {w.paypal_fee && (
                  <p data-testid="withdrawal-fee" className="mt-1 text-[11px] text-chrome-dim">
                    {t.feeRecorded(w.paypal_fee.display)}
                  </p>
                )}
              </td>
              {state === "pending" && (
                <td className={tdClass}>
                  {w.state === "pending" && (
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => onPaid(w)}
                        className={`${smallButton} border-money/50 text-money hover:bg-money hover:text-ground`}
                      >
                        {t.markPaid}
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => onReject(w)}
                        className={`${smallButton} border-alert/50 text-alert hover:bg-alert hover:text-ground`}
                      >
                        {t.reject}
                      </button>
                    </div>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// MarkPaidDialog is the one place the PayPal fee is typed. It prefills the
// server's list-price fee; an empty input sends no fee at all and the server
// records that same list price.
export function MarkPaidDialog({
  withdrawal,
  onConfirm,
  onCancel,
}: {
  withdrawal: AdminWithdrawal;
  onConfirm: (paypalFeeCents: number | null) => void;
  onCancel: () => void;
}) {
  const t = copy.admin.withdrawals;
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [fee, setFee] = useState(
    withdrawal.default_paypal_fee ? (withdrawal.default_paypal_fee.cents / 100).toFixed(2) : "",
  );
  const [bad, setBad] = useState(false);
  useEffect(() => {
    cancelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onCancel();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);
  const confirm = () => {
    const typed = fee.trim();
    if (typed === "") return onConfirm(null);
    const cents = hundredths(typed);
    if (cents === null) return setBad(true);
    onConfirm(cents);
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-ground/80 px-4 backdrop-blur-sm">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="admin-paid-text"
        className="w-full max-w-md rounded-2xl border border-ground-edge bg-ground-raised p-6"
      >
        <p id="admin-paid-text" className="text-sm leading-relaxed text-chrome">
          {copy.admin.confirmPaid(withdrawal.amount_display, withdrawal.paypal_email)}
        </p>
        <div className="mt-5">
          <label htmlFor="paypal-fee" className={labelClass}>
            {t.paypalFee}
          </label>
          <input
            id="paypal-fee"
            type="text"
            inputMode="decimal"
            value={fee}
            onChange={(e) => {
              setFee(e.target.value);
              setBad(false);
            }}
            className={inputClass}
          />
          <p className={hintClass}>{t.feeHint}</p>
          {bad && (
            <p role="alert" className="mt-1.5 text-[12px] text-alert">
              {t.feeInvalid}
            </p>
          )}
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button ref={cancelRef} type="button" onClick={onCancel} className={ghostButton}>
            {copy.admin.cancel}
          </button>
          <button type="button" onClick={confirm} className={primaryButton}>
            {copy.admin.confirm}
          </button>
        </div>
      </div>
    </div>
  );
}

// NetByMonth is rake against what the rails cost, a month per row. Every
// figure is the server's display string, in the server's order.
function NetByMonth({ periods, totals }: { periods: PlatformPeriod[]; totals: Platform["totals"] }) {
  const t = copy.admin.platform;
  const c = t.net.columns;
  return (
    <section data-testid="platform-periods">
      <h3 className="mb-3 text-[11px] font-bold uppercase tracking-[0.16em] text-gold">{t.net.title}</h3>
      <div className={tableWrap}>
        <table className="w-full min-w-[640px] border-collapse">
          <thead className="border-b border-ground-edge">
            <tr>
              {[c.month, c.rake, c.card, c.paypal, c.net].map((h) => (
                <th key={h} className={thClass}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {periods.map((p) => (
              <tr key={p.month} data-testid="platform-period" className={rowClass}>
                <td className={tdClass}>
                  <p className="font-bold">{p.label}</p>
                  {p.estimated_paypal_fees > 0 && (
                    <p className="mt-1 text-[11px] text-chrome-dim">{t.net.estimated(p.estimated_paypal_fees)}</p>
                  )}
                </td>
                <td className={`${tdClass} bf-tabular text-money`}>{p.rake.display}</td>
                <td className={`${tdClass} bf-tabular`}>{p.card_fees.display}</td>
                <td className={`${tdClass} bf-tabular`}>{p.paypal_fees.display}</td>
                <td className={`${tdClass} bf-tabular font-bold`}>{p.net.display}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="border-t-2 border-ground-edge">
            <tr data-testid="platform-period-totals">
              <td className={`${tdClass} font-bold uppercase tracking-[0.12em]`}>{t.totals}</td>
              {[totals.rake, totals.card_fees, totals.paypal_fees, totals.net].map((m, i) => (
                <td key={i} className={`${tdClass} bf-tabular font-bold`}>
                  {m.display}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}

// Every figure, totals included, is the server's display string.
export function PlatformTable({ platform }: { platform: Platform }) {
  const t = copy.admin.platform;
  const none = t.none;
  const c = t.columns;
  const tot = platform.totals;
  const net = platform.fees_unavailable ? (
    <p role="status" className="mb-4 rounded-md border border-gold/40 bg-gold/10 px-4 py-3 text-[13px] text-chrome">
      {t.net.unavailable}
    </p>
  ) : platform.periods.length > 0 ? (
    <div className="mb-6">
      <NetByMonth periods={platform.periods} totals={tot} />
    </div>
  ) : null;
  if (platform.rows.length === 0)
    return (
      <>
        {net}
        <Empty icon="barChart" title={t.empty} />
      </>
    );
  return (
    <>
      {net}
      {platform.markets_unavailable && (
        <p role="status" className="mb-4 rounded-md border border-gold/40 bg-gold/10 px-4 py-3 text-[13px] text-chrome">
          {t.marketsUnavailable}
        </p>
      )}
      <div className={tableWrap}>
        <table className="w-full min-w-[980px] border-collapse">
          <thead className="border-b border-ground-edge">
            <tr>
              {[c.contest, c.contestants, c.pot, c.revenue, c.prize, c.paid, c.rake, c.cut, c.dust].map((h) => (
                <th key={h} className={thClass}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {platform.rows.map((r) => (
              <tr key={r.contest_id} data-testid="platform-row" className={rowClass}>
                <td className={tdClass}>
                  <a href={`/contest/${r.slug || r.contest_id}`} className="bf-focusable font-bold text-gold hover:text-gold-light">
                    {r.title}
                  </a>
                  {r.seeded && (
                    <span className="ml-2">
                      <Chip tone="dim">{t.seeded}</Chip>
                    </span>
                  )}
                </td>
                <td className={`${tdClass} bf-tabular`}>{r.contestants}</td>
                <td className={`${tdClass} bf-tabular`}>{r.pot.display}</td>
                <td className={`${tdClass} bf-tabular`}>{r.revenue.display}</td>
                <td className={`${tdClass} bf-tabular`}>{r.cash_prize.display}</td>
                <td className={`${tdClass} bf-tabular`}>{r.winner_paid.display}</td>
                <td className={`${tdClass} bf-tabular text-money`}>{r.market?.rake.display ?? none}</td>
                <td className={`${tdClass} bf-tabular`}>{r.market?.producer_cut.display ?? none}</td>
                <td className={`${tdClass} bf-tabular`}>{r.market?.dust.display ?? none}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="border-t-2 border-ground-edge">
            <tr data-testid="platform-totals">
              <td className={`${tdClass} font-bold uppercase tracking-[0.12em]`}>{t.totals}</td>
              <td className={tdClass} />
              {[tot.pot, tot.revenue, tot.cash_prize, tot.winner_paid, tot.rake, tot.producer_cut, tot.dust].map((m, i) => (
                <td key={i} className={`${tdClass} bf-tabular font-bold`}>
                  {m.display}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
    </>
  );
}

// Usage: signup tiles, one CSS bar per month, and the two tables. No chart
// library: the bars are widths.
export function UsagePanel({ usage }: { usage: Usage }) {
  const t = copy.admin.usage;
  const s = usage.signups;
  const peak = Math.max(1, ...s.monthly.map((m) => m.count));
  const tiles: [string, number][] = [
    [t.tiles.last7, s.last7],
    [t.tiles.last30, s.last30],
    [t.tiles.last90, s.last90],
    [t.tiles.ytd, s.ytd],
    [t.tiles.total, s.total],
  ];
  return (
    <div className="flex flex-col gap-8">
      <div>
        <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.16em] text-chrome-dim">
          {t.signups} · {t.queried(shortDate(usage.queried_at))}
        </p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {tiles.map(([label, n]) => (
            <div key={label} data-testid="usage-tile" className="rounded-xl border border-ground-edge bg-ground px-4 py-3">
              <p className="text-[10px] uppercase tracking-[0.16em] text-chrome-dim">{label}</p>
              <p className="bf-tabular font-headline text-3xl leading-none text-chrome">{n}</p>
            </div>
          ))}
        </div>
      </div>

      <div>
        <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.16em] text-chrome-dim">{t.monthly}</p>
        {s.monthly.length === 0 ? (
          <p className="text-[13px] text-chrome-dim">{t.empty}</p>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {s.monthly.map((m) => (
              <li key={m.month} data-testid="month-bar" className="grid grid-cols-[64px_minmax(0,1fr)_48px] items-center gap-3 text-[12px]">
                <span className="text-chrome-dim">{m.month}</span>
                <span className="h-3 rounded-sm bg-ground-edge">
                  <span className="block h-3 rounded-sm bg-money" style={{ width: `${(m.count / peak) * 100}%` }} />
                </span>
                <span className="bf-tabular text-right text-chrome">{m.count}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.16em] text-chrome-dim">{t.recent}</p>
        <div className={tableWrap}>
          <table className="w-full min-w-[640px] border-collapse">
            <thead className="border-b border-ground-edge">
              <tr>
                {[t.columns.user, t.columns.email, t.columns.joined, t.columns.verified, t.columns.type].map((h) => (
                  <th key={h} className={thClass}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {s.recent_signups.map((r) => (
                <tr key={`${r.email}-${r.createdAt}`} data-testid="recent-signup" className={rowClass}>
                  <td className={tdClass}>{r.username}</td>
                  <td className={tdClass}>{r.email}</td>
                  <td className={tdClass}>{shortDate(r.createdAt)}</td>
                  <td className={tdClass}>{r.emailVerified ? t.yes : t.no}</td>
                  <td className={tdClass}>{r.type}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.16em] text-chrome-dim">
          {t.funnel} · {t.siteConfigs(usage.editor.site_configs)}
        </p>
        {usage.editor.funnel.length === 0 ? (
          <p className="text-[13px] text-chrome-dim">{t.empty}</p>
        ) : (
          <div className={tableWrap}>
            <table className="w-full min-w-[640px] border-collapse">
              <thead className="border-b border-ground-edge">
                <tr>
                  {[t.columns.user, t.columns.email, t.columns.loads, t.columns.publishes, t.columns.purchases].map((h) => (
                    <th key={h} className={thClass}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {usage.editor.funnel.map((f) => (
                  <tr key={f.user_id} className={rowClass}>
                    <td className={tdClass}>{f.username}</td>
                    <td className={tdClass}>{f.email}</td>
                    <td className={`${tdClass} bf-tabular`}>{f.loads}</td>
                    <td className={`${tdClass} bf-tabular`}>{f.publishes}</td>
                    <td className={`${tdClass} bf-tabular`}>{f.purchase_attempts}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Bars({ rows, testId }: { rows: GoalCount[]; testId: string }) {
  const peak = Math.max(1, ...rows.map((r) => r.count));
  return (
    <ul className="flex flex-col gap-1.5">
      {rows.map((r) => (
        <li key={r.label} data-testid={testId} className="grid grid-cols-[96px_minmax(0,1fr)_40px] items-center gap-3 text-[12px]">
          <span className="truncate text-chrome-dim">{r.label}</span>
          <span className="h-3 rounded-sm bg-ground-edge">
            <span className="block h-3 rounded-sm bg-money" style={{ width: `${(r.count / peak) * 100}%` }} />
          </span>
          <span className="bf-tabular text-right text-chrome">{r.count}</span>
        </li>
      ))}
    </ul>
  );
}

// The earnings calculator's saved goals: how many people, what they want to
// make, and in which genres. Every figure is the server's.
export function GoalsPanel({ goals }: { goals: Goals }) {
  const t = copy.admin.goals;
  if (goals.savers === 0) return <Empty icon="handCoin" title={t.empty} />;
  const tiles: [string, number][] = [
    [t.tiles.savers, goals.savers],
    [t.tiles.members, goals.members],
    [t.tiles.visitors, goals.visitors],
  ];
  const label = (role: string) => (role === "producer" ? t.producers : t.fans);
  return (
    <div className="flex flex-col gap-8">
      <div className="grid grid-cols-3 gap-3">
        {tiles.map(([name, n]) => (
          <div key={name} data-testid="goals-tile" className="rounded-xl border border-ground-edge bg-ground px-4 py-3">
            <p className="text-[10px] uppercase tracking-[0.16em] text-chrome-dim">{name}</p>
            <p className="bf-tabular font-headline text-3xl leading-none text-chrome">{n}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {goals.roles.map((r) => (
          <div key={r.role} data-testid={`goals-role-${r.role}`} className="rounded-xl border border-ground-edge bg-ground p-4">
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-chrome">
              {label(r.role)} <span className="bf-tabular text-gold">({r.savers})</span>
            </p>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div>
                <p className="text-[10px] uppercase tracking-[0.16em] text-chrome-dim">{t.median}</p>
                <p className="bf-tabular font-headline text-2xl leading-none text-money">{r.median.display}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.16em] text-chrome-dim">{t.total}</p>
                <p className="bf-tabular font-headline text-2xl leading-none text-chrome">{r.total.display}</p>
              </div>
            </div>
            <div className="mt-4">
              <Bars rows={r.buckets} testId="goals-bucket" />
            </div>
            {r.role === "producer" && r.savers > 0 && (
              <p className="mt-3 text-[12px] text-chrome-dim">
                {t.money} <span className="bf-tabular text-chrome">{r.money}</span> · {t.buzz}{" "}
                <span className="bf-tabular text-chrome">{r.buzz}</span>
              </p>
            )}
          </div>
        ))}
      </div>

      <div>
        <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.16em] text-chrome-dim">{t.genres}</p>
        <Bars rows={goals.genres} testId="goals-genre" />
      </div>

      <div>
        <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.16em] text-chrome-dim">{t.recent}</p>
        <div className={tableWrap}>
          <table className="w-full min-w-[640px] border-collapse">
            <thead className="border-b border-ground-edge">
              <tr>
                {[t.columns.who, t.columns.role, t.columns.goal, t.columns.genre, t.columns.focus, t.columns.when].map((h) => (
                  <th key={h} className={thClass}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {goals.recent.map((g, i) => (
                <tr key={i} data-testid="goals-row" className={rowClass}>
                  <td className={tdClass}>{g.username ? `@${g.username}` : <span className="text-chrome-dim">{t.visitor}</span>}</td>
                  <td className={tdClass}>{label(g.role)}</td>
                  <td className={`${tdClass} bf-tabular text-money`}>{g.goal.display}</td>
                  <td className={tdClass}>{g.genre || t.anyGenre}</td>
                  <td className={tdClass}>{g.focus === "buzz" ? t.buzz : g.focus === "money" ? t.money : ""}</td>
                  <td className={tdClass}>{shortDate(g.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
